/**
 * @typedef {Object} WorkerMessage
 * @property {string} type - The message type/action name
 * @property {*} payload - The message payload data
 */

/**
 * @typedef {Object} WorkerRegistration
 * @property {Worker} worker - The Web Worker instance
 * @property {Map<string, Set<Function>>} listeners - Map of message types to listener sets
 */

/**
 * Singleton class that manages Web Workers and their message listeners.
 * Provides a centralized registry for workers with type-based message routing.
 *
 * @example
 * // Register a worker
 * const manager = WorkerManager.getInstance()
 * const worker = new Worker(new URL('./zones.worker.js', import.meta.url))
 * manager.registerWorker('zones', worker)
 *
 * // Subscribe to messages in useEffect
 * useEffect(() => {
 *   const manager = WorkerManager.getInstance()
 *   const handleUpdate = (data) => console.log('Zone updated:', data)
 *
 *   manager.addEventListener('zones', 'UPDATE_ZONE', handleUpdate)
 *   return () => manager.removeEventListener('zones', 'UPDATE_ZONE', handleUpdate)
 * }, [])
 *
 * // Send message to worker
 * manager.postMessage('zones', { type: 'UPDATE_ZONE', payload: { id: 123 } })
 */
class WorkerManager {
  constructor() {
    /** @type {Map<string, WorkerRegistration>} */
    this.workers = new Map()
  }

  /**
   * Gets the singleton instance of WorkerManager.
   * @returns {WorkerManager} The singleton instance
   */
  static getInstance() {
    if (!WorkerManager.instance) {
      WorkerManager.instance = new WorkerManager()
    }
    return WorkerManager.instance
  }

  /**
   * Registers a Web Worker with a unique key.
   * Sets up the worker's message handler to route messages to registered listeners.
   *
   * @param {string} workerKey - The unique identifier for this worker
   * @param {Worker} worker - The Web Worker instance
   * @throws {Error} If a worker with the given key is already registered
   */
  registerWorker(workerKey, worker) {
    if (this.workers.has(workerKey)) {
      throw new Error(`Worker with key "${workerKey}" is already registered`)
    }

    const registration = {
      worker,
      listeners: new Map(),
    }

    // Set up message handler to route to type-specific listeners
    worker.addEventListener("message", event => {
      const message = event.data
      if (!message || typeof message.type !== "string") {
        console.warn(`Worker "${workerKey}" sent invalid message format:`, message)
        return
      }

      const listeners = registration.listeners.get(message.type)
      if (!listeners || listeners.size === 0) {
        return
      }

      // Create a copy to avoid issues if listeners are modified during iteration
      const listenersCopy = [...listeners]
      listenersCopy.forEach(listener => {
        try {
          listener(message.payload)
        } catch (error) {
          console.error(`Error in listener for "${workerKey}.${message.type}":`, error)
        }
      })
    })

    // Handle worker errors
    worker.addEventListener("error", event => {
      console.error(`Worker "${workerKey}" error:`, event.message, event)
    })

    this.workers.set(workerKey, registration)
  }

  /**
   * Sends a message to a registered worker.
   *
   * @param {string} workerKey - The unique identifier for the worker
   * @param {WorkerMessage} message - The message object with type and payload
   * @throws {Error} If the worker is not registered
   */
  postMessage(workerKey, message) {
    const registration = this.workers.get(workerKey)
    if (!registration) {
      throw new Error(`Worker "${workerKey}" is not registered`)
    }

    registration.worker.postMessage(message)
  }

  /**
   * Adds an event listener for a specific message type from a worker.
   * Multiple listeners can be added for the same message type.
   *
   * @param {string} workerKey - The unique identifier for the worker
   * @param {string} messageType - The message type to listen for
   * @param {Function} handler - The callback function that receives the message payload
   * @throws {Error} If the worker is not registered
   */
  addEventListener(workerKey, messageType, handler) {
    const registration = this.workers.get(workerKey)
    if (!registration) {
      throw new Error(`Worker "${workerKey}" is not registered`)
    }

    if (!registration.listeners.has(messageType)) {
      registration.listeners.set(messageType, new Set())
    }

    const listeners = registration.listeners.get(messageType)
    listeners.add(handler)
  }

  /**
   * Removes an event listener for a specific message type from a worker.
   *
   * @param {string} workerKey - The unique identifier for the worker
   * @param {string} messageType - The message type to stop listening for
   * @param {Function} handler - The callback function to remove
   */
  removeEventListener(workerKey, messageType, handler) {
    const registration = this.workers.get(workerKey)
    if (!registration) {
      return
    }

    const listeners = registration.listeners.get(messageType)
    if (!listeners) {
      return
    }

    listeners.delete(handler)
    if (listeners.size === 0) {
      registration.listeners.delete(messageType)
    }
  }

  /**
   * Checks if a worker is registered with the given key.
   *
   * @param {string} workerKey - The unique identifier for the worker
   * @returns {boolean} True if the worker is registered, false otherwise
   */
  isRegistered(workerKey) {
    return this.workers.has(workerKey)
  }

  /**
   * Terminates a worker and removes all its listeners.
   * After calling this, the worker must be re-registered to be used again.
   *
   * @param {string} workerKey - The unique identifier for the worker
   */
  terminate(workerKey) {
    const registration = this.workers.get(workerKey)
    if (!registration) {
      return
    }

    registration.worker.terminate()
    registration.listeners.clear()
    this.workers.delete(workerKey)
  }
}

export default WorkerManager
